import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Mail, Server, Hash, User, AtSign, Type, Lock, Eye, EyeOff, ShieldAlert,
  Loader2, CheckCircle2, AlertCircle, Send, ShieldCheck, ChevronDown,
  KeyRound, Sparkles, Trash2, Database, FileCode, Copy, Check, Plug,
  CircleDot, XCircle, Plus,
} from "lucide-react";
import Input from "@/components/ui/Input";
import Button from "@/components/ui/Button";
import { cn } from "@/lib/cn";
// The application-wide toast store — the same one the Support inbox, the
// changelog editor and the usage report use. It auto-dismisses on its own
// timer, which is exactly what the SMTP tab's hand-rolled inline banner did
// not do: that one sat there until it was clicked shut.
import { toastSuccess, toastError } from "@/lib/toast";
// Maps a rejected request's per-field messages onto form state. Already used
// by the changelog editor — the SMTP form reuses it rather than parsing the
// validation envelope a second time.
import { fieldErrors } from "@/api/client";
import TesterSettings from "@/pages/dashboard/Settings";
import ConfirmDialog from "@/components/dashboard/ConfirmDialog";
import LoadError from "@/components/dashboard/LoadError";
import { useApiData } from "@/hooks/useApiData";
import {
  listSmtpPlatforms, listSmtpConfigs, saveSmtpConfig, activateSmtpConfig,
  deleteSmtpConfig, testSmtpConfig, testLiveSender,
  getOAuthConfig, updateOAuthConfig,
  getAiConfig, updateAiConfig, deleteAiConfig, testAiConfig,
} from "@/api/admin";
import { formatDateTime } from "@/lib/dates";

// Provider-specific gotchas. The host/port defaults come from the backend
// catalog (/admin/smtp-platforms) — these are just the human hints that stop the
// most common misconfiguration per provider.
const PLATFORM_HINTS = {
  brevo: "Username is your SMTP login (e.g. xxxx@smtp-brevo.com) — not your Brevo account email. Password is the generated SMTP key (Brevo → SMTP & API → SMTP), not the API key. The sender must be verified in Brevo.",
  sendgrid: "Username is literally \"apikey\". Password is your SendGrid API key. The sender must pass Single Sender Verification or domain auth.",
  gmail: "Use a 16-char App Password (not your login password). Gmail rewrites the From to the authed account. ~500 emails/day cap.",
  ses: "Use your SES SMTP credentials (generated in the SES console — NOT your AWS access keys). The host is region-specific, e.g. email-smtp.us-east-1.amazonaws.com.",
  mailgun: "Username is the full SMTP login (e.g. postmaster@mg.yourdomain.com). The sending domain must be verified in Mailgun.",
  resend: "Username is \"resend\". Password is your Resend API key. The sender domain must be verified in Resend.",
  custom: "Enter your provider's SMTP host, port and credentials manually.",
};

const FIELD_CLS =
  "h-11 w-full rounded-xl border border-slate-300 bg-white px-4 text-sm text-slate-900 placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-4 focus:ring-brand-500/15 dark:border-white/10 dark:bg-white/5 dark:text-white dark:placeholder:text-slate-500";

// Reuses the shared Settings toast look.
function Toast({ type, message, onClose }) {
  return (
    <AnimatePresence>
      {message && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          className={cn(
            "mb-5 flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm",
            type === "error"
              ? "border-rose-200 bg-rose-50/70 text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-200"
              : "border-emerald-200 bg-emerald-50/70 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-200"
          )}
        >
          {type === "error" ? <AlertCircle size={15} className="shrink-0" /> : <CheckCircle2 size={15} className="shrink-0" />}
          <span className="flex-1">{message}</span>
          <button type="button" onClick={onClose} className="text-xs font-medium opacity-60 hover:opacity-100">✕</button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function EncryptionBanner({ what }) {
  return (
    <div className="flex items-start gap-2.5 rounded-xl border border-rose-200 bg-rose-50/70 px-4 py-3 text-sm text-rose-700 dark:border-rose-500/25 dark:bg-rose-500/10 dark:text-rose-200">
      <ShieldAlert size={16} className="mt-0.5 shrink-0" />
      <span>
        <b>CREDENTIALS_FERNET_KEY</b> is not set on the server. Generate one
        (<code className="rounded bg-rose-100 px-1 dark:bg-rose-500/20">python -c &quot;from cryptography.fernet import Fernet; print(Fernet.generate_key().decode())&quot;</code>),
        add it to the environment and restart — saving is blocked until then, since {what} can&apos;t be encrypted.
      </span>
    </div>
  );
}

// Shown only for useApiData's `demo` flag (backend unreachable). Every other
// failure surfaces through <LoadError /> instead, so a 403/500 is never mistaken
// for "nothing configured".
function DemoBanner() {
  return (
    <div className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-2.5 text-sm text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200">
      <AlertCircle size={15} className="shrink-0" /> Showing demo data — backend not reachable.
    </div>
  );
}

const fmtDate = (v) => formatDateTime(v, "never");

// ---------------------------------------------------------------------------
// Multi-provider SMTP. Several providers stay configured; exactly one is active.
// ---------------------------------------------------------------------------
const BLANK_FORM = {
  platform: "brevo", label: "", host: "", port: 587, username: "",
  sender_email: "", sender_name: "AI-QA-Engine", secure: false, enabled: true,
};

// What a provider must carry, mirroring _missing_on_create() in admin_smtp.py.
//
// These four plus the password are what it takes to SEND: somewhere to connect
// (host, port), an identity to authenticate as (username, password) and an
// address to send from (sender_email). `label`, `sender_name`, `secure` and
// `enabled` all have working defaults and are genuinely optional — none of them
// gets a star.
//
// `isEditing` is the one conditional: on an existing provider a blank password
// means "keep the stored one", which is the only way to edit a provider whose
// password can never be read back. Requiring it on edit would make every
// configured provider uneditable.
function validateSmtp(form, password, isEditing) {
  const errors = {};
  const host = (form.host || "").trim();
  const username = (form.username || "").trim();
  const senderEmail = (form.sender_email || "").trim();
  const port = Number(form.port);

  if (!host) errors.host = "SMTP host is required.";
  if (`${form.port ?? ""}`.trim() === "") errors.port = "Port is required.";
  else if (!Number.isInteger(port) || port < 1 || port > 65535) {
    errors.port = "Port must be a whole number between 1 and 65535.";
  }
  if (!username) errors.username = "SMTP username is required.";
  if (!senderEmail) errors.sender_email = "A sender email address is required.";
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(senderEmail)) {
    errors.sender_email = "Enter a valid sender email address.";
  }
  if (!isEditing && !password.trim()) {
    errors.password = "An SMTP password is required for a new provider.";
  }
  return errors;
}

function SmtpTab() {
  const { data: platforms, demo: platformsDemo, error: platformsError, reload: reloadPlatforms } = useApiData(listSmtpPlatforms, []);
  const { data: configs, demo, error: configsError, reload } = useApiData(listSmtpConfigs, []);
  const list = Array.isArray(configs) ? configs : [];
  // Memoised: the `[]` branch is a fresh array every render, which made the
  // `byId` useMemo below recompute on every render.
  const catalog = useMemo(() => (Array.isArray(platforms) ? platforms : []), [platforms]);
  // A 403/500 leaves both lists null; without this the tab claims "No providers
  // configured yet" over a live, working mail provider.
  const loadError = configsError || platformsError;

  const [form, setForm] = useState(BLANK_FORM);
  const [password, setPassword] = useState(""); // only sent when the admin types one
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [testingId, setTestingId] = useState(null);
  const [testingLive, setTestingLive] = useState(false);
  const [actingId, setActingId] = useState(null);
  // Deleting a mail provider is irreversible and can promote a different one to
  // active; it used to fire on a single unconfirmed click.
  const [pendingDelete, setPendingDelete] = useState(null);
  // Set as soon as the admin edits anything, so a row action (test / activate /
  // delete) reloading `configs` can't overwrite a half-typed provider.
  const [dirty, setDirty] = useState(false);
  // The editor is CLOSED on arrival. It used to be rendered unconditionally, and
  // the re-sync effect below fills it from the saved config — so opening the tab
  // dropped the admin straight into a populated edit form over a provider they
  // had only come to look at, one stray keystroke away from changing it.
  // Opening is now an explicit act: "Edit" on a row, or "Add a provider".
  const [editorOpen, setEditorOpen] = useState(false);
  // Per-field messages, keyed by the same names the API uses — so a rejection
  // from the server drops straight in through fieldErrors() with no translation.
  const [fieldErrs, setFieldErrs] = useState({});

  const byId = useMemo(() => Object.fromEntries(catalog.map((p) => [p.id, p])), [catalog]);
  const selected = byId[form.platform];
  // Every saved config carries the flag, so one missing key blocks the whole tab.
  const encryptionMissing = list.some((c) => c.encryption_available === false);
  const editing = list.find((c) => c.platform === form.platform) || null;

  // Default the dropdown to the first catalog entry once it loads. BLANK_FORM
  // starts on a platform the catalog does contain, so the preset also has to be
  // applied for the initial selection — otherwise the host the page promises
  // "fills in automatically" stays empty and the save is blocked on it.
  useEffect(() => {
    if (!catalog.length) return;
    if (!byId[form.platform]) pickPlatform(catalog[0].id);
    else if (!form.host) pickPlatform(form.platform);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog.length]);

  const set = (key) => (e) => {
    setDirty(true);
    setForm((f) => ({ ...f, [key]: e.target.value }));
    // Clear this field's message as soon as it is touched — leaving it up while
    // the admin fixes it reads as "still wrong".
    setFieldErrs((p) => (p[key] ? { ...p, [key]: undefined } : p));
  };

  // Open the editor on a specific provider ("Edit" on a row), or on a blank one
  // ("Add a provider"). Separate from pickPlatform, which is also called by the
  // re-sync effect and by the in-form platform dropdown — neither of which
  // should be able to open the editor by itself.
  function openEditor(platformId) {
    // "Add a provider" (no id) must start on a platform that is NOT already
    // configured. Without this it opened on whichever platform was last edited,
    // pre-filled with that provider's saved settings — so "Add" silently became
    // "edit the existing one", and saving updated a provider the admin thought
    // they were creating.
    const target = platformId
      || (catalog.find((p) => !list.some((c) => c.platform === p.id)) || catalog[0] || {}).id;
    if (target) pickPlatform(target);
    setFieldErrs({});
    setEditorOpen(true);
  }

  // Leave the editor without saving. The form is re-seeded from the stored
  // config on the next open, so an abandoned edit never lingers.
  function closeEditor() {
    setEditorOpen(false);
    setDirty(false);
    setPassword("");
    setFieldErrs({});
    if (form.platform) pickPlatform(form.platform);
  }

  // Selecting a platform loads its saved config if there is one, otherwise
  // pre-fills the catalog defaults so only credentials are left to type.
  function pickPlatform(id) {
    const preset = byId[id] || {};
    const saved = list.find((c) => c.platform === id);
    setPassword("");
    setDirty(false);
    // Switching platform re-seeds every field, so messages about the previous
    // one no longer describe anything on screen.
    setFieldErrs({});
    setForm(saved
      ? {
          platform: id, label: saved.label || "", host: saved.host || "", port: saved.port,
          username: saved.username || "", sender_email: saved.sender_email || "",
          sender_name: saved.sender_name || "", secure: !!saved.secure, enabled: !!saved.enabled,
        }
      : {
          ...BLANK_FORM, platform: id, label: preset.label || "",
          host: preset.host || "", port: preset.port ?? 587, secure: !!preset.secure,
        });
  }

  // Re-sync the form when the list reloads (on mount, and after save/delete).
  //
  // This used to read `if (saved && !editing)`, but `editing` is the very same
  // lookup as `saved` — so the condition could never be true and the form never
  // hydrated at all. The saved host/username/sender were therefore invisible:
  // the tab rendered an empty "add new" form over an existing configuration.
  //
  // Every row action (test / activate / delete) calls reload(), which always
  // yields a new array identity — so this must stand off a form the admin is
  // part-way through, including the typed password that nothing restores.
  useEffect(() => {
    if (dirty) return;
    if (!list.length || !form.platform) return;
    if (list.some((c) => c.platform === form.platform)) pickPlatform(form.platform);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configs]);

  async function save(e) {
    e.preventDefault();
    if (encryptionMissing || busy) return;
    // The form had no required-field checks and the backend accepts empty
    // strings, so saving a half-filled card silently wiped the working host /
    // username / sender on the active mail provider.
    const host = (form.host || "").trim();
    const username = (form.username || "").trim();
    const senderEmail = (form.sender_email || "").trim();
    const port = Number(form.port);

    // EVERY field at once, not the first failure. These checks used to be a
    // chain of `return toastError(...)`, so a form with three empty fields
    // reported one, and the admin discovered the next only after fixing it —
    // and none of the messages was attached to the box it was about.
    const problems = validateSmtp(form, password, !!editing);
    if (Object.keys(problems).length) {
      setFieldErrs(problems);
      // Nothing is sent. The editor stays open with the typed values intact.
      const first = ["host", "port", "username", "password", "sender_email"]
        .find((k) => problems[k]);
      if (first) {
        document.getElementById(`smtp-${first}`)?.focus();
      }
      return;
    }
    setFieldErrs({});
    setBusy(true);
    try {
      const payload = {
        platform: form.platform,
        label: form.label || undefined,
        host,
        port,
        username,
        sender_email: senderEmail,
        sender_name: (form.sender_name || "").trim(),
        secure: !!form.secure,
        enabled: !!form.enabled,
      };
      if (password) payload.password = password; // blank = keep the stored one
      await saveSmtpConfig(payload);
      setPassword("");
      setDirty(false);
      await reload();
      // Only now — the row above is repainted from the reloaded config, so the
      // confirmation describes what the server actually stored.
      setEditorOpen(false);
      toastSuccess(`Email/SMTP settings saved — ${selected?.label || form.platform}.`);
    } catch (err) {
      // The editor stays OPEN on failure, with the typed values intact, so a
      // rejected save is something to correct rather than to retype.
      //
      // The server is the authority on what a provider needs, and it names the
      // fields it refused — put those messages on the inputs rather than in a
      // banner that makes the admin guess which one.
      const fromServer = fieldErrors(err);
      if (Object.keys(fromServer).length) {
        setFieldErrs((p) => ({ ...p, ...fromServer }));
        return;
      }
      toastError((err && err.message) || "Failed to save.");
    } finally {
      setBusy(false);
    }
  }

  async function activate(c) {
    setActingId(c.id);
    try {
      await activateSmtpConfig(c.id);
      await reload();
      toastSuccess(`${c.platform_label} is now sending all email.`);
    } catch (err) {
      toastError((err && err.message) || "Could not activate.");
    } finally {
      setActingId(null);
    }
  }

  async function remove() {
    const c = pendingDelete;
    if (!c) return;
    setActingId(c.id);
    try {
      const res = await deleteSmtpConfig(c.id);
      setPendingDelete(null);
      await reload();
      if (form.platform === c.platform) {
        // `list` inside this closure is still the pre-delete array, so
        // pickPlatform would re-fill the form from the row just deleted — under
        // an "Add a provider" heading that re-creates it on save.
        const preset = byId[c.platform] || {};
        setPassword("");
        setDirty(false);
        setForm({
          ...BLANK_FORM, platform: c.platform, label: preset.label || "",
          host: preset.host || "", port: preset.port ?? 587, secure: !!preset.secure,
        });
      }
      toastSuccess(res.promoted
        ? `${c.platform_label} deleted — ${res.promoted.platform_label} is now active.`
        : `${c.platform_label} deleted.`);
    } catch (err) {
      toastError((err && err.message) || "Could not delete.");
    } finally {
      setActingId(null);
    }
  }

  // Diagnose the sender the app is REALLY using. The per-provider test needs a
  // stored row; a deployment configured purely through environment variables has
  // none, so this was the one question the settings page couldn't answer:
  // "why is signup verification failing on live?"
  async function testLive() {
    setTestingLive(true);
    try {
      const res = await testLiveSender();
      if (res.sent) {
        toastSuccess(`Sent via ${res.host}:${res.port} as ${res.from} — check ${res.to}.`);
      } else {
        // The reason is the whole point: an auth rejection and a blocked port
        // look identical from the signup form and need opposite fixes.
        toastError(`Not sent — ${res.reason || "unknown reason"}`
          + (res.configured && !res.active ? " (credentials are set but sending is switched off)" : ""));
      }
    } catch (err) {
      toastError((err && err.message) || "Live test failed.");
    } finally {
      setTestingLive(false);
    }
  }

  async function test(c) {
    setTestingId(c.id);
    try {
      const res = await testSmtpConfig(c.id);
      await reload();
      toastSuccess(`${c.platform_label}: connected and authenticated${res.sent_to ? ` — test email sent to ${res.sent_to}` : ""}.`);
    } catch (err) {
      await reload(); // the failure reason is persisted on the row
      toastError(`${c.platform_label}: ${(err && err.message) || "test failed"}`);
    } finally {
      setTestingId(null);
    }
  }

  return (
    <div className="space-y-5">
      <LoadError message={loadError} onRetry={() => { reload(); reloadPlatforms(); }} className="mb-0" />
      {(demo || platformsDemo) && !loadError && <DemoBanner />}
      {encryptionMissing && <EncryptionBanner what="SMTP passwords" />}

      {/* saved providers */}
      <div className="card p-6">
        <div className="flex items-center gap-3">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-brand-500/10 text-brand-600 dark:text-brand-300"><Mail size={18} /></span>
          <div>
            <h3 className="text-base font-semibold text-slate-800 dark:text-white">Email providers</h3>
            <p className="mt-0.5 text-sm text-slate-400">Keep several configured and switch the active sender in one click — no redeploy. All outbound email (invites, resets, notifications) goes through whichever is active.</p>
          </div>
          <Button
            variant="outline" size="sm" className="ml-auto shrink-0"
            onClick={testLive} disabled={testingLive}
            title="Send a test through whatever this deployment is actually using"
          >
            {testingLive ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
            Test live sender
          </Button>
        </div>

        {loadError ? null : list.length === 0 ? (
          // The editor no longer sits open underneath, so the empty state has to
          // offer the way in rather than point at a form that isn't there.
          <div className="mt-5 rounded-xl border border-dashed border-slate-200 p-5 text-center dark:border-white/10">
            <p className="text-sm text-slate-400">
              No providers configured yet. The first one you save becomes the active sender.
            </p>
            {!editorOpen && (
              <Button type="button" variant="outline" size="sm" className="mt-3"
                onClick={() => openEditor(catalog[0]?.id)}>
                <Plus size={15} /> Add a provider
              </Button>
            )}
          </div>
        ) : (
          <div className="mt-5 space-y-3">
            {list.map((c) => (
              <div key={c.id} className={cn(
                "flex flex-col gap-3 rounded-2xl border p-4 sm:flex-row sm:items-center sm:justify-between",
                c.is_active
                  ? "border-emerald-300 bg-emerald-50/40 dark:border-emerald-500/30 dark:bg-emerald-500/5"
                  : "border-slate-200 dark:border-white/10"
              )}>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-semibold text-slate-800 dark:text-white">{c.platform_label}</p>
                    {c.is_active && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs font-semibold text-emerald-700 dark:text-emerald-300">
                        <CircleDot size={11} /> Active
                      </span>
                    )}
                    {!c.enabled && (
                      <span className="rounded-full bg-slate-200 px-2 py-0.5 text-xs font-semibold text-slate-600 dark:bg-white/10 dark:text-slate-300">Disabled</span>
                    )}
                    {c.last_test_error ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-rose-500/15 px-2 py-0.5 text-xs font-semibold text-rose-700 dark:text-rose-300">
                        <XCircle size={11} /> Test failed
                      </span>
                    ) : c.last_tested_at ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-sky-500/15 px-2 py-0.5 text-xs font-semibold text-sky-700 dark:text-sky-300">
                        <ShieldCheck size={11} /> Tested OK
                      </span>
                    ) : null}
                  </div>
                  <p className="mt-1 truncate text-xs text-slate-400">
                    {c.host}:{c.port} · {c.username || "no username"} · from {c.sender_email || "—"}
                  </p>
                  <p className="mt-0.5 text-xs text-slate-400">
                    {c.password_set
                      ? <>Password <code className="rounded bg-slate-100 px-1 dark:bg-white/10">{c.password_hint || "••••"}</code></>
                      : c.uses_env_password
                        ? "Using the server's env password (GMAIL_APP_PASSWORD)"
                        : "No password set"}
                    {" · tested "}{fmtDate(c.last_tested_at)}
                  </p>
                  {c.last_test_error && (
                    <p className="mt-1 break-words text-xs text-rose-600 dark:text-rose-300">{c.last_test_error}</p>
                  )}
                </div>

                <div className="flex shrink-0 items-center gap-2">
                  <button type="button" onClick={() => openEditor(c.platform)}
                    className="h-9 rounded-lg border border-slate-200 px-3 text-xs font-semibold text-slate-600 hover:bg-slate-50 dark:border-white/10 dark:text-slate-300 dark:hover:bg-white/5">
                    Edit
                  </button>
                  <button type="button" onClick={() => test(c)} disabled={testingId === c.id}
                    className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 px-3 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-60 dark:border-white/10 dark:text-slate-300 dark:hover:bg-white/5">
                    {testingId === c.id ? <Loader2 size={13} className="animate-spin" /> : <Plug size={13} />} Test
                  </button>
                  {!c.is_active && (
                    <button type="button" onClick={() => activate(c)} disabled={actingId === c.id}
                      className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-brand-500 px-3 text-xs font-semibold text-white hover:bg-brand-600 disabled:opacity-60">
                      {actingId === c.id ? <Loader2 size={13} className="animate-spin" /> : <CircleDot size={13} />} Set active
                    </button>
                  )}
                  <button type="button" onClick={() => setPendingDelete(c)} disabled={actingId === c.id}
                    title={c.is_active ? "Active — another provider is promoted automatically" : "Delete"}
                    className="grid h-9 w-9 place-items-center rounded-lg text-rose-500 hover:bg-rose-50 disabled:opacity-60 dark:hover:bg-rose-500/10">
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add another provider — the way into the editor when one or more are
          already configured. Hidden while the editor is open, so there is only
          ever one obvious next action. */}
      {!loadError && list.length > 0 && !editorOpen && (
        <Button type="button" variant="outline" onClick={() => openEditor(null)}>
          <Plus size={16} /> Add a provider
        </Button>
      )}

      {/* add / edit one provider — only once the admin has asked for it */}
      {editorOpen && (
      // noValidate is what lets THIS form's own validation run at all.
      //
      // Input forwards `required` to the DOM (that is how it earns its asterisk
      // and its aria-required), and a required-but-empty input makes the browser
      // refuse to submit — no submit event, so save() never fires and none of
      // the field messages below ever render. The native bubble that replaces
      // them is unstyled, one-at-a-time and not the app's wording.
      //
      // Turning off NATIVE enforcement only: `required` still marks the fields
      // for assistive technology, and validateSmtp() — which the server mirrors
      // — is what actually decides.
      <form onSubmit={save} noValidate className="space-y-5">
        <div className="card p-6">
          <h3 className="text-base font-semibold text-slate-800 dark:text-white">
            {editing ? `Edit ${selected?.label || form.platform}` : "Add a provider"}
          </h3>
          <p className="mt-0.5 text-sm text-slate-400">
            Pick a platform — the host and port fill in automatically. One saved config per platform, so saving again updates it.
          </p>

          <div className="mt-6 space-y-5">
            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Platform</span>
              <span className="relative block">
                <select value={form.platform} onChange={(e) => pickPlatform(e.target.value)}
                  className={cn(FIELD_CLS, "appearance-none pr-10")}>
                  {catalog.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.label}{list.some((c) => c.platform === p.id) ? " — configured" : ""}
                    </option>
                  ))}
                </select>
                <ChevronDown size={16} className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
              </span>
              <span className="mt-1.5 block text-xs text-slate-400">{PLATFORM_HINTS[form.platform] || ""}</span>
            </label>

            {/* `required` on Input renders the same rose asterisk every other
                required field in the app uses, and wires aria-required /
                aria-invalid / aria-describedby with the error — no new pattern
                and no second validation framework. */}
            <div className="grid gap-5 sm:grid-cols-2">
              <Input id="smtp-host" label="SMTP Host" required icon={Server} placeholder="smtp-relay.brevo.com"
                value={form.host || ""} onChange={set("host")} error={fieldErrs.host} />
              <div>
                <Input id="smtp-port" label="Port" required icon={Hash} type="number" placeholder="587"
                  value={form.port ?? ""} onChange={set("port")} error={fieldErrs.port} />
                {!fieldErrs.port && (
                  <span className="mt-1.5 block text-xs text-slate-400">465 (SSL) or 587 (STARTTLS).</span>
                )}
              </div>
            </div>

            <Input id="smtp-username" label="SMTP Username" required icon={User} placeholder="SMTP login"
              value={form.username || ""} onChange={set("username")} error={fieldErrs.username} autoComplete="off" />

            <label className="block" htmlFor="smtp-password">
              <span className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
                SMTP Password / API key
                {/* Starred only while ADDING. On an existing provider blank
                    means "keep the stored password", which is genuinely
                    optional — a star there would be a rule that isn't real. */}
                {!editing && <span className="text-rose-500" aria-hidden="true"> *</span>}
              </span>
              <span className="relative block">
                <Lock size={18} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  id="smtp-password"
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => {
                    setDirty(true);
                    setPassword(e.target.value);
                    setFieldErrs((p) => (p.password ? { ...p, password: undefined } : p));
                  }}
                  placeholder={editing?.password_set ? `${editing.password_hint || "••••••••"} (unchanged)` : "SMTP key / app password"}
                  autoComplete="new-password"
                  disabled={encryptionMissing}
                  aria-required={!editing ? "true" : undefined}
                  aria-invalid={fieldErrs.password ? "true" : undefined}
                  aria-describedby={fieldErrs.password ? "smtp-password-error" : undefined}
                  className={cn(FIELD_CLS, "pl-11 pr-11", encryptionMissing && "opacity-60",
                    fieldErrs.password && "border-rose-400 focus:border-rose-500 focus:ring-rose-500/15")}
                />
                {/* Open eye = the secret is readable right now, matching every
                    other reveal toggle in the app. Label states the action. */}
                <button type="button" onClick={() => setShowPw((s) => !s)} aria-label={showPw ? "Hide SMTP password" : "Show SMTP password"} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
                  {showPw ? <Eye size={16} /> : <EyeOff size={16} />}
                </button>
              </span>
              {fieldErrs.password ? (
                <span id="smtp-password-error" role="alert" className="mt-1.5 block text-xs font-medium text-rose-500">
                  {fieldErrs.password}
                </span>
              ) : (
                <span className="mt-1.5 block text-xs text-slate-400">
                  Stored encrypted (Fernet / AES). Never shown again after saving.
                  {editing?.password_set && " Leave blank to keep the current one."}
                </span>
              )}
            </label>

            {/* The root cause of "clicking the row toggles the checkbox": a
                <label> is a block-level flex container here, so it spanned the
                full width of the form and EVERY pixel of that row — including
                the empty space beside the text and the explanatory sentence —
                was label, and a click anywhere on a label activates its control.

                The control and its name are now wrapped in an `inline-flex w-fit`
                label, which is only as wide as the words it contains, and the
                guidance sentence has been moved OUT of the label into a
                described-by hint. So the checkbox and the text "Use implicit TLS
                (SSL)" still toggle it — the standard, screen-reader-friendly
                behaviour — while the rest of the row does not.

                Explicit id/htmlFor rather than an implicit wrap, so the
                association survives the label no longer enclosing everything. */}
            <div className="flex flex-col gap-1">
              <label htmlFor="smtp-secure" className="inline-flex w-fit cursor-pointer items-center gap-2.5">
                <input
                  id="smtp-secure"
                  type="checkbox"
                  checked={!!form.secure}
                  aria-describedby="smtp-secure-hint"
                  onChange={(e) => { setDirty(true); setForm((f) => ({ ...f, secure: e.target.checked })); }}
                  className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500/30 dark:border-white/20 dark:bg-white/5"
                />
                <span className="text-sm text-slate-600 dark:text-slate-300">Use implicit TLS (SSL)</span>
              </label>
              <span id="smtp-secure-hint" className="pl-[1.625rem] text-xs text-slate-400">
                On for port 465, off for 587 STARTTLS.
              </span>
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <Input id="smtp-sender_email" label="Sender Email" required icon={AtSign} type="email"
                  placeholder="noreply@yourdomain.com" value={form.sender_email || ""}
                  onChange={set("sender_email")} error={fieldErrs.sender_email} />
                {!fieldErrs.sender_email && (
                  <span className="mt-1.5 block text-xs text-slate-400">Must be a verified/authorized sender on the provider.</span>
                )}
              </div>
              {/* Sender Name is genuinely optional — the backend defaults it and
                  mail sends fine without one, so it gets no star. */}
              <Input label="Sender Name" icon={Type} placeholder="AI-QA-Engine" value={form.sender_name || ""} onChange={set("sender_name")} />
            </div>

            {/* Same defect as the TLS row above — a full-width <label> turning
                the whole strip, divider included, into a toggle target. Same
                fix, so the two checkboxes in this form behave identically. */}
            <div className="flex flex-col gap-1 border-t border-slate-100 pt-4 dark:border-white/5">
              <label htmlFor="smtp-enabled" className="inline-flex w-fit cursor-pointer items-center gap-2.5">
                <input
                  id="smtp-enabled"
                  type="checkbox"
                  checked={!!form.enabled}
                  aria-describedby="smtp-enabled-hint"
                  onChange={(e) => { setDirty(true); setForm((f) => ({ ...f, enabled: e.target.checked })); }}
                  className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500/30 dark:border-white/20 dark:bg-white/5"
                />
                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Enabled</span>
              </label>
              <span id="smtp-enabled-hint" className="pl-[1.625rem] text-xs text-slate-400">
                This provider may be used to send.
              </span>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Button type="submit" disabled={busy || encryptionMissing}>
            {busy ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
            {editing ? "Save changes" : "Add provider"}
          </Button>
          {/* Cancel returns to the view state without writing anything. */}
          <Button type="button" variant="outline" onClick={closeEditor} disabled={busy}>
            Cancel
          </Button>
          <span className="inline-flex items-center gap-1.5 text-xs text-slate-400">
            <ShieldCheck size={14} /> Test a provider from its row above — you can test one before making it active.
          </span>
        </div>
      </form>
      )}

      <ConfirmDialog
        open={pendingDelete}
        title={pendingDelete ? `Delete ${pendingDelete.platform_label}?` : ""}
        body={pendingDelete && pendingDelete.is_active
          ? "This is the ACTIVE mail provider. Deleting it promotes another configured provider to active; if there is none, the app falls back to the .env credentials and outbound email may stop. This cannot be undone."
          : "This removes the provider and its stored credentials. This cannot be undone."}
        confirmLabel="Delete provider"
        busy={!!pendingDelete && actingId === pendingDelete.id}
        onConfirm={remove}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Google OAuth credentials. Change the client id/secret without a redeploy.
// ---------------------------------------------------------------------------
const OAUTH_DEFAULTS = {
  client_id: "", secret_hint: "", secret_set: false, enabled: true,
  redirect_uri: "", source: "none", encryption_available: true,
};

const OAUTH_SOURCE = {
  database: { label: "Dashboard (saved here)", icon: Database, cls: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-300" },
  env: { label: "Server .env file", icon: FileCode, cls: "bg-sky-500/10 text-sky-600 dark:text-sky-300" },
  none: { label: "Not configured", icon: AlertCircle, cls: "bg-rose-500/10 text-rose-600 dark:text-rose-300" },
};

function OAuthTab() {
  const { data, demo, error, reload } = useApiData(getOAuthConfig, OAUTH_DEFAULTS);
  const [info, setInfo] = useState(OAUTH_DEFAULTS);
  const [clientId, setClientId] = useState("");
  const [redirectUri, setRedirectUri] = useState("");
  const [secret, setSecret] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (data) {
      setInfo({ ...OAUTH_DEFAULTS, ...data });
      setClientId(data.client_id || "");
      setRedirectUri(data.redirect_uri || "");
    }
  }, [data]);

  const encryptionMissing = info.encryption_available === false;
  const source = OAUTH_SOURCE[info.source] || OAUTH_SOURCE.none;

  async function copyRedirect() {
    try {
      await navigator.clipboard.writeText(redirectUri || info.redirect_uri);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard unavailable */ }
  }

  async function save(e) {
    e.preventDefault();
    if (encryptionMissing) return;
    setBusy(true);
    try {
      const payload = { client_id: clientId.trim(), enabled: !!info.enabled };
      // Blank = keep the stored URI, same as the secret below. The server reads
      // an empty string as an explicit clear, which would break every Google
      // login with redirect_uri_mismatch if this field never hydrated.
      if (redirectUri.trim()) payload.redirect_uri = redirectUri.trim();
      if (secret.trim()) payload.client_secret = secret.trim(); // blank = keep stored
      const updated = await updateOAuthConfig(payload);
      setInfo((i) => ({ ...i, ...updated }));
      setSecret("");
      await reload();
      setToast({ type: "success", message: "Saved. Google sign-in uses these credentials from the next login — no restart." });
    } catch (err) {
      setToast({ type: "error", message: (err && err.message) || "Failed to save the OAuth credentials." });
    } finally {
      setBusy(false);
    }
  }

  // The form is never rendered against an unhydrated config: a 403/500 would
  // otherwise show blank credentials as "Not configured" and let a save
  // overwrite the working ones.
  if (error) return <LoadError message={error} onRetry={reload} />;

  return (
    <form onSubmit={save} className="space-y-5">
      <Toast {...(toast || {})} onClose={() => setToast(null)} />
      {demo && <DemoBanner />}
      {encryptionMissing && <EncryptionBanner what="the client secret" />}

      <div className="card p-6">
        <div className="flex items-center gap-3">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-brand-500/10 text-brand-600 dark:text-brand-300"><ShieldCheck size={18} /></span>
          <div>
            <h3 className="text-base font-semibold text-slate-800 dark:text-white">Google sign-in</h3>
            <p className="mt-0.5 text-sm text-slate-400">
              Credentials for the &quot;Sign in with Google&quot; button. Create a <b>Web application</b> OAuth client in the Google Cloud console and paste it here. The secret is stored encrypted and never sent back to this page.
            </p>
          </div>
        </div>

        {/* current status */}
        <div className="mt-5 flex flex-col gap-3 rounded-2xl border border-slate-100 p-4 dark:border-white/5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <span className={cn("grid h-9 w-9 shrink-0 place-items-center rounded-xl", source.cls)}><source.icon size={16} /></span>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Credential source</p>
              <p className="text-sm font-semibold text-slate-800 dark:text-white">{source.label}</p>
            </div>
          </div>
          <code className="rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-600 dark:bg-white/10 dark:text-slate-300">
            {info.secret_set ? (info.secret_hint || "secret set") : "no secret configured"}
          </code>
        </div>

        {/* redirect URL — the #1 setup mistake, so make it copyable */}
        <div className="mt-5 rounded-2xl border border-sky-200 bg-sky-50/60 p-4 dark:border-sky-500/20 dark:bg-sky-500/5">
          <p className="text-sm font-medium text-sky-900 dark:text-sky-200">Authorized redirect URI</p>
          <p className="mt-0.5 text-xs text-sky-700/80 dark:text-sky-300/80">
            Edit if needed, then paste it <b>exactly</b> into your Google OAuth client → &quot;Authorized redirect URIs&quot;. It&apos;s replayed during the token exchange, so any mismatch fails with <code>redirect_uri_mismatch</code>. Must point at this backend&apos;s <code>/auth/google/callback</code>.
          </p>
          <div className="mt-2.5 flex items-center gap-2 rounded-xl border border-sky-200 bg-white p-2 dark:border-sky-500/20 dark:bg-white/5">
            <input
              type="text"
              value={redirectUri}
              onChange={(e) => setRedirectUri(e.target.value)}
              placeholder="https://your-backend/auth/google/callback"
              autoComplete="off" spellCheck="false"
              className="flex-1 bg-transparent px-1.5 font-mono text-xs text-slate-800 outline-none dark:text-white"
            />
            <button type="button" onClick={copyRedirect} className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-white/10">
              {copied ? <Check size={15} className="text-emerald-500" /> : <Copy size={15} />}
            </button>
          </div>
          <p className="mt-1.5 text-[0.6875rem] text-sky-700/70 dark:text-sky-300/70">
            Saved here, applied live — no redeploy. Source: {info.redirect_uri_source === "database" ? "saved in dashboard" : "env / default"}.
          </p>
        </div>

        <div className="mt-5 space-y-5">
          <div>
            <Input label="Client ID" icon={KeyRound} placeholder="1234567890-abc123.apps.googleusercontent.com"
              value={clientId} onChange={(e) => setClientId(e.target.value)} autoComplete="off" spellCheck="false" />
            <span className="mt-1.5 block text-xs text-slate-400">Not a secret — it&apos;s visible in the consent URL.</span>
          </div>

          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Client Secret</span>
            <span className="relative block">
              <Lock size={18} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type={showSecret ? "text" : "password"}
                value={secret}
                onChange={(e) => setSecret(e.target.value)}
                placeholder={info.secret_set ? `${info.secret_hint || "••••••••"} (unchanged)` : "GOCSPX-…"}
                autoComplete="new-password"
                spellCheck="false"
                disabled={encryptionMissing}
                className={cn(FIELD_CLS, "pl-11 pr-11 font-mono", encryptionMissing && "opacity-60")}
              />
              <button type="button" onClick={() => setShowSecret((s) => !s)} aria-label={showSecret ? "Hide client secret" : "Show client secret"} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
                {showSecret ? <Eye size={16} /> : <EyeOff size={16} />}
              </button>
            </span>
            <span className="mt-1.5 block text-xs text-slate-400">
              Stored encrypted; never shown again after saving.
              {info.secret_set && " Leave blank to keep the current one."}
            </span>
          </label>

          <label className="flex cursor-pointer items-center gap-2.5 border-t border-slate-100 pt-4 dark:border-white/5">
            <input type="checkbox" checked={!!info.enabled} onChange={(e) => setInfo((i) => ({ ...i, enabled: e.target.checked }))}
              className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500/30 dark:border-white/20 dark:bg-white/5" />
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
              Enable Google sign-in <span className="font-normal text-slate-400">(off hides the button on the login page)</span>
            </span>
          </label>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" disabled={busy || encryptionMissing}>
          {busy ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />} Save credentials
        </Button>
        <span className="inline-flex items-center gap-1.5 text-xs text-slate-400">
          <ShieldCheck size={14} /> Test it for real: sign out and use the Google button on the login page.
        </span>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// AI provider key (Anthropic / Claude). Paste a key here and every agent run uses
// it instead of the ANTHROPIC_API_KEY baked into the server env.
// ---------------------------------------------------------------------------
const AI_DEFAULTS = {
  key_set: false, key_preview: "", source: "none",
  env_key_set: false, encryption_available: true, updated_at: null,
};

const SOURCE_META = {
  database: { label: "Dashboard (this key)", icon: Database, cls: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-300" },
  env: { label: "Server .env file", icon: FileCode, cls: "bg-sky-500/10 text-sky-600 dark:text-sky-300" },
  none: { label: "Not configured", icon: AlertCircle, cls: "bg-rose-500/10 text-rose-600 dark:text-rose-300" },
};

// ---------------------------------------------------------------------------
// Active AI — the master switch. Flips EVERY agent (pipeline + exe + extension +
// vision + repair + assistant + testgen) between Claude and ChatGPT in one click,
// and holds the OpenAI key next to the Claude key. Backed by /admin/ai-config
// (active_provider), the single source of truth for the active AI.
// ---------------------------------------------------------------------------
const AI_OPTIONS = [
  { k: "anthropic", label: "Claude", sub: "Anthropic" },
  { k: "openai", label: "ChatGPT", sub: "OpenAI" },
  { k: "ollama_cloud", label: "Ollama", sub: "Ollama Cloud" },
  // Share the work across every saved key and route around one that stops answering.
  // Needs two keys to mean anything, so it stays disabled until then.
  { k: "auto", label: "Auto", sub: "share all keys" },
];
// Short + long provider labels, so the switch messages aren't a pile of ternaries.
const PROVIDER_SHORT = { anthropic: "Claude", openai: "OpenAI", ollama_cloud: "Ollama" };
const PROVIDER_LONG = {
  anthropic: "Claude (Anthropic)", openai: "ChatGPT (OpenAI)", ollama_cloud: "Ollama (Cloud)",
  auto: "Auto — shared across every saved key",
};

// What the Settings sidebar says under "AI / API Key". Derived from the SAME
// active_provider the panel below switches on, so the two can never disagree —
// it used to be the hardcoded string "Claude key", which was simply wrong for
// every deployment running on OpenAI or Ollama.
const PROVIDER_HINT = {
  anthropic: "Claude key", openai: "ChatGPT key", ollama_cloud: "Ollama key",
  auto: "All provider keys",
};
export function providerHint(active) {
  // Unknown or not yet loaded → provider-neutral, never a guess at a brand.
  return PROVIDER_HINT[active] || "AI provider keys";
}

// Whether a provider's key card belongs on screen.
//
// Only the provider actually in force — selecting ChatGPT used to leave the
// Claude and Ollama key forms sitting underneath it, so the page showed three
// sets of credentials for one active provider.
//
// "auto" is the deliberate exception, and not a loophole: auto IS every saved
// key, rotating between them, so its keys are all in use and all have to remain
// manageable. Showing one of them would leave the others unreachable while they
// were actively serving traffic.
function showsProvider(active, provider) {
  return active === provider || active === "auto";
}

// `data` / `reload` come from AiKeyTab: both render /admin/ai-config, and a
// second loader here meant saving or removing the Claude key never refreshed
// this panel — the switch kept reporting "no key" for a key just saved, and kept
// offering a provider whose key had just been deleted.
// `active` / `setActive` are OWNED BY AdminSettings and threaded down, not held
// here. The settings sidebar has to name the provider that is actually in force,
// and a copy of it living in this panel would be a second source of truth that
// drifts the moment someone switches provider — which is exactly how the sidebar
// came to advertise "Claude key" while OpenAI was the active provider.
function ActiveAiPanel({ data, reload, active, setActive }) {
  const [openaiKey, setOpenaiKey] = useState("");
  const [ollamaKey, setOllamaKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [showOllamaKey, setShowOllamaKey] = useState(false);
  const [busy, setBusy] = useState(false);
  const [savingKey, setSavingKey] = useState(false);
  const [savingOllama, setSavingOllama] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testingOllama, setTestingOllama] = useState(false);
  const [toast, setToast] = useState(null);
  // Load-balancing dials. Seeded from whatever is actually in force on the server
  // (saved here, or env, or the shipped default) so the form never invents numbers.
  const [weights, setWeights] = useState({});
  const [slots, setSlots] = useState({});
  const [savingBalance, setSavingBalance] = useState(false);

  useEffect(() => {
    if (!data) return;
    setWeights(data.provider_weights || {});
    setSlots(data.provider_slots || {});
  }, [data]);

  // The share each key would get, as a percentage — the numbers are relative, so the
  // only honest way to show "how much is this really" is against the running total.
  const weightTotal = Object.values(weights)
    .reduce((sum, v) => sum + (Number(v) || 0), 0);
  const sharePct = (k) => {
    const v = Number(weights[k]) || 0;
    if (!weightTotal || !v) return "—";
    return `${Math.round((v / weightTotal) * 100)}%`;
  };

  async function saveBalance() {
    setSavingBalance(true);
    try {
      const num = (o) => Object.fromEntries(
        Object.entries(o).map(([k, v]) => [k, Number(v) || 1]));
      await updateAiConfig({ provider_weights: num(weights), provider_slots: num(slots) });
      setToast({ type: "success", message: "Load balancing saved — it applies to new cases right away." });
      await reload();
    } catch (e) {
      setToast({ type: "error", message: (e && e.message) || "Could not save the balancing." });
    } finally { setSavingBalance(false); }
  }

  function resetBalance() {
    setWeights(data.provider_weights || {});
    setSlots(data.provider_slots || {});
  }

  const anthropicReady = !!(data && data.key_set);
  const openaiReady = !!(data && data.openai_key_set);
  const ollamaReady = !!(data && data.ollama_key_set);
  const encryptionReady = !data || data.encryption_available !== false;
  // Auto rotates across whatever the server says is keyed, so it needs two or more.
  // The server enforces this too — this only keeps the button from offering a save
  // that would come straight back as an error.
  const autoPool = (data && data.auto_pool) || [];
  const autoReady = !!(data && data.auto_available);
  const benched = (data && data.benched) || {};
  const ready = {
    anthropic: anthropicReady, openai: openaiReady, ollama_cloud: ollamaReady,
    auto: autoReady,
  };

  async function switchTo(p) {
    if (busy || p === active) return;
    if (!ready[p]) {
      setToast({
        type: "error",
        message: p === "auto"
          ? "Auto shares work between two or more providers — save a second key first."
          : `Add a ${PROVIDER_SHORT[p] || p} key first.`,
      });
      return;
    }
    setBusy(true);
    try {
      await updateAiConfig({ active_provider: p });
      setActive(p);
      setToast({
        type: "success",
        message: p === "auto"
          ? `Work is now shared across ${autoPool.map((x) => PROVIDER_SHORT[x] || x).join(", ")}.`
          : `Every AI agent now runs on ${PROVIDER_LONG[p] || p}.`,
      });
      await reload();
    } catch (e) {
      setToast({ type: "error", message: (e && e.message) || "Switch failed." });
    } finally { setBusy(false); }
  }

  async function saveOpenaiKey(e) {
    if (e && e.preventDefault) e.preventDefault();
    if (!openaiKey.trim()) return;
    if (!encryptionReady) {
      setToast({ type: "error", message: "Server has no encryption key (CREDENTIALS_FERNET_KEY) — cannot store the key." });
      return;
    }
    setSavingKey(true);
    try {
      await updateAiConfig({ openai_api_key: openaiKey.trim() });
      setOpenaiKey("");
      setToast({ type: "success", message: "OpenAI key saved. You can now switch to ChatGPT." });
      await reload();
    } catch (err) {
      setToast({ type: "error", message: (err && err.message) || "Failed to save the OpenAI key." });
    } finally { setSavingKey(false); }
  }

  async function saveOllamaKey(e) {
    if (e && e.preventDefault) e.preventDefault();
    if (!ollamaKey.trim()) return;
    if (!encryptionReady) {
      setToast({ type: "error", message: "Server has no encryption key (CREDENTIALS_FERNET_KEY) — cannot store the key." });
      return;
    }
    setSavingOllama(true);
    try {
      await updateAiConfig({ ollama_api_key: ollamaKey.trim() });
      setOllamaKey("");
      setToast({ type: "success", message: "Ollama key saved. You can now switch to Ollama." });
      await reload();
    } catch (err) {
      setToast({ type: "error", message: (err && err.message) || "Failed to save the Ollama key." });
    } finally { setSavingOllama(false); }
  }

  async function testOpenai() {
    setTesting(true);
    try {
      const r = await testAiConfig("openai");
      setToast({ type: "success", message: `OpenAI key is valid (model ${r.model}).` });
    } catch (err) {
      setToast({ type: "error", message: (err && err.message) || "OpenAI key test failed." });
    } finally { setTesting(false); }
  }

  async function testOllama() {
    setTestingOllama(true);
    try {
      const r = await testAiConfig("ollama_cloud");
      setToast({ type: "success", message: `Ollama key is valid (model ${r.model}).` });
    } catch (err) {
      setToast({ type: "error", message: (err && err.message) || "Ollama key test failed." });
    } finally { setTestingOllama(false); }
  }

  const activeLabel = PROVIDER_LONG[active]
    || "Mixed — per-agent routing (set on AI Providers)";

  return (
    <div className="card p-6">
      <Toast {...(toast || {})} onClose={() => setToast(null)} />

      <div className="flex items-center gap-3">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-brand-500/10 text-brand-600 dark:text-brand-300"><Sparkles size={18} /></span>
        <div>
          <h3 className="text-base font-semibold text-slate-800 dark:text-white">Active AI</h3>
          <p className="mt-0.5 text-sm text-slate-400">One switch moves every agent — pipeline, exe, extension, vision, repair and the assistant — between Claude and ChatGPT.</p>
        </div>
      </div>

      {/* the switch */}
      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="inline-flex rounded-xl border border-slate-200 bg-slate-50 p-1 dark:border-white/10 dark:bg-white/5">
          {AI_OPTIONS.map((opt) => (
            <button key={opt.k} type="button" disabled={busy} onClick={() => switchTo(opt.k)}
              className={cn("flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-60",
                active === opt.k
                  ? "bg-white text-brand-600 shadow-sm dark:bg-white/10 dark:text-brand-300"
                  : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200")}>
              {busy && active !== opt.k ? <Loader2 size={14} className="animate-spin" /> : null}
              {opt.label}
              <span className="text-[0.6875rem] font-normal opacity-60">{opt.sub}</span>
              {!ready[opt.k] && (
                <span className="ml-1 text-[0.625rem] font-medium text-rose-500">
                  {opt.k === "auto" ? "needs 2 keys" : "no key"}
                </span>
              )}
            </button>
          ))}
        </div>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Currently: <span className="font-semibold text-slate-800 dark:text-white">{activeLabel}</span>
        </p>
      </div>

      {/* Live pool state. Without this, a key sitting out is invisible: throughput
          quietly drops while the page still implies every provider is in use. */}
      {active === "auto" && (
        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
          {autoPool.map((p) => (
            <span key={p} className={cn(
              "inline-flex items-center gap-1.5 rounded-lg px-2 py-1 font-medium",
              benched[p]
                ? "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300"
                : "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300")}>
              {PROVIDER_SHORT[p] || p}
              {benched[p]
                ? ` — resting ${Math.ceil(benched[p] / 60)} min`
                : " — in use"}
            </span>
          ))}
          <span className="text-slate-400">
            Calls rotate across these. One that hits its limit sits out and rejoins on its own.
          </span>
        </div>
      )}

      {/* Load balancing. Only meaningful in Auto — with a single provider selected
          every call goes there and both dials are ignored. */}
      {active === "auto" && (
        <div className="mt-6 border-t border-slate-100 pt-5 dark:border-white/5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Load balancing</span>
            <span className="text-[0.6875rem] text-slate-400">
              from {data && data.weights_source === "database" ? "this page" : "server config"}
            </span>
          </div>
          <p className="mt-1 text-xs text-slate-400">
            Set two numbers per provider and press Save. It applies to the next test case — no restart.
          </p>

          <div className="mt-4 space-y-2">
            <div className="flex items-center gap-3 px-1 text-[0.625rem] font-bold uppercase tracking-wider text-slate-400">
              <span className="w-28">Provider</span>
              <span className="w-20 text-center">Share</span>
              <span className="w-16 text-right">= %</span>
              <span className="w-24 text-center">At once</span>
            </div>
            {AI_OPTIONS.filter((o) => o.k !== "auto").map((o) => (
              <div key={o.k} className="flex items-center gap-3">
                <span className="w-28 text-sm font-medium text-slate-700 dark:text-slate-200">
                  {o.label}
                  {!ready[o.k] && <span className="ml-1 text-[0.625rem] text-rose-500">no key</span>}
                </span>
                <input
                  value={weights[o.k] ?? ""} inputMode="numeric" aria-label={`${o.label} share`}
                  onChange={(e) => setWeights({ ...weights, [o.k]: e.target.value.replace(/\D/g, "") })}
                  className={cn(FIELD_CLS, "h-9 w-20 text-center tabular-nums")}
                />
                <span className="w-16 text-right text-sm tabular-nums text-slate-500 dark:text-slate-400">
                  {sharePct(o.k)}
                </span>
                <input
                  value={slots[o.k] ?? ""} inputMode="numeric" aria-label={`${o.label} calls at once`}
                  onChange={(e) => setSlots({ ...slots, [o.k]: e.target.value.replace(/\D/g, "") })}
                  className={cn(FIELD_CLS, "h-9 w-24 text-center tabular-nums")}
                />
              </div>
            ))}
          </div>

          {/* Three lines, not a manual. The two numbers look interchangeable and are not —
              one splits the work, the other is a capacity limit that makes things WORSE when
              set too high — so each gets one plain sentence and a concrete example. */}
          <div className="mt-4 rounded-xl bg-slate-50 p-3.5 dark:bg-white/5">
            <p className="text-xs font-semibold text-slate-600 dark:text-slate-300">How this works</p>
            <ul className="mt-2 space-y-1.5 text-[0.6875rem] leading-relaxed text-slate-500 dark:text-slate-400">
              <li>
                <b className="text-slate-600 dark:text-slate-300">Share</b> — how much work each key
                gets. 75 / 25 / 1 means about 74 cases to ChatGPT, 25 to Ollama and 1 to Claude out
                of every 100.
              </li>
              <li>
                <b className="text-slate-600 dark:text-slate-300">At once</b> — how many calls that
                key can run in parallel. Don’t set it higher than the plan allows — extra calls just
                fail.
              </li>
              <li>A key that stops working is skipped automatically. Minimum share is 1.</li>
            </ul>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-3">
            <Button type="button" onClick={saveBalance} disabled={savingBalance}>
              {savingBalance ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />} Save balancing
            </Button>
            <Button type="button" variant="outline" onClick={resetBalance} disabled={savingBalance}>
              Reset to server config
            </Button>
            <span className="text-xs text-slate-400">Applies to new cases immediately — no restart.</span>
          </div>
        </div>
      )}

      {/* OpenAI key — NOT a <form> (this panel renders inside the Claude-key form,
          and nested forms are invalid HTML → the submit handler would be dropped).
          Rendered only when OpenAI is the provider in force. */}
      {showsProvider(active, "openai") && (
      <div className="mt-6 border-t border-slate-100 pt-5 dark:border-white/5">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-slate-700 dark:text-slate-300">OpenAI / ChatGPT API key</span>
          {openaiReady
            ? <span className="rounded-lg bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">key set</span>
            : <span className="rounded-lg bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-500 dark:bg-white/10 dark:text-slate-400">no key</span>}
        </div>
        <span className="mt-2 relative block">
          <KeyRound size={18} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type={showKey ? "text" : "password"}
            value={openaiKey}
            onChange={(e) => setOpenaiKey(e.target.value)}
            placeholder={openaiReady ? "•••••• (leave blank to keep)" : "sk-…"}
            autoComplete="off" spellCheck="false" disabled={!encryptionReady}
            className={cn(FIELD_CLS, "pl-11 pr-11 font-mono", !encryptionReady && "opacity-60")}
          />
          <button type="button" onClick={() => setShowKey((s) => !s)} aria-label={showKey ? "Hide API key" : "Show API key"} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
            {showKey ? <Eye size={16} /> : <EyeOff size={16} />}
          </button>
        </span>
        <span className="mt-1.5 block text-xs text-slate-400">
          From platform.openai.com → API keys. Stored encrypted. Model is set on the AI Providers page. Managed together with per-agent routing there.
        </span>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <Button type="button" onClick={saveOpenaiKey} disabled={savingKey || !openaiKey.trim() || !encryptionReady}>
            {savingKey ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />} Save OpenAI key
          </Button>
          <Button type="button" variant="outline" onClick={testOpenai} disabled={testing || !openaiReady}>
            {testing ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />} Test key
          </Button>
        </div>
      </div>
      )}

      {/* Ollama Cloud key — same pattern as the OpenAI block above. */}
      {showsProvider(active, "ollama_cloud") && (
      <div className="mt-6 border-t border-slate-100 pt-5 dark:border-white/5">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Ollama Cloud API key</span>
          {ollamaReady
            ? <span className="rounded-lg bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">key set</span>
            : <span className="rounded-lg bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-500 dark:bg-white/10 dark:text-slate-400">no key</span>}
        </div>
        <span className="mt-2 relative block">
          <KeyRound size={18} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type={showOllamaKey ? "text" : "password"}
            value={ollamaKey}
            onChange={(e) => setOllamaKey(e.target.value)}
            placeholder={ollamaReady ? "•••••• (leave blank to keep)" : "id.secret"}
            autoComplete="off" spellCheck="false" disabled={!encryptionReady}
            className={cn(FIELD_CLS, "pl-11 pr-11 font-mono", !encryptionReady && "opacity-60")}
          />
          <button type="button" onClick={() => setShowOllamaKey((s) => !s)} aria-label={showOllamaKey ? "Hide API key" : "Show API key"} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
            {showOllamaKey ? <Eye size={16} /> : <EyeOff size={16} />}
          </button>
        </span>
        <span className="mt-1.5 block text-xs text-slate-400">
          From ollama.com → Settings → Keys (paid Ollama Cloud). Stored encrypted. Per-agent models are set on the Model Config page.
        </span>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <Button type="button" onClick={saveOllamaKey} disabled={savingOllama || !ollamaKey.trim() || !encryptionReady}>
            {savingOllama ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />} Save Ollama key
          </Button>
          <Button type="button" variant="outline" onClick={testOllama} disabled={testingOllama || !ollamaReady}>
            {testingOllama ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />} Test key
          </Button>
        </div>
      </div>
      )}
    </div>
  );
}

// `ai` is the /admin/ai-config load, made by AdminSettings and shared with the
// sidebar; `active` / `setActive` are the one piece of provider state the whole
// tab (and the sidebar hint) reads from.
function AiKeyTab({ ai, active, setActive }) {
  const { data, demo, error, reload } = ai;
  const [info, setInfo] = useState(AI_DEFAULTS);
  const [key, setKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [removing, setRemoving] = useState(false);
  // The key is stored encrypted and never read back, so removing it is
  // irreversible — and with no .env fallback it stops every agent run.
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => { if (data) setInfo({ ...AI_DEFAULTS, ...data }); }, [data]);

  const encryptionMissing = info.encryption_available === false;
  const source = SOURCE_META[info.source] || SOURCE_META.none;

  async function save(e) {
    e.preventDefault();
    if (!key.trim() || encryptionMissing) return;
    setBusy(true);
    try {
      const updated = await updateAiConfig({ anthropic_api_key: key.trim() });
      setInfo((i) => ({ ...i, ...updated }));
      setKey("");
      await reload(); // keeps the Active AI switch above in step with the new key
      setToast({ type: "success", message: "API key saved. New agent runs will use it." });
    } catch (err) {
      setToast({ type: "error", message: (err && err.message) || "Failed to save the API key." });
    } finally {
      setBusy(false);
    }
  }

  async function test() {
    setTesting(true);
    try {
      const res = await testAiConfig();
      setToast({ type: "success", message: `Key is valid — Anthropic accepted it (${res.key_preview}).` });
    } catch (err) {
      setToast({ type: "error", message: (err && err.message) || "Key test failed." });
    } finally {
      setTesting(false);
    }
  }

  async function remove() {
    setRemoving(true);
    try {
      const updated = await deleteAiConfig();
      setInfo((i) => ({ ...i, ...updated }));
      setConfirmRemove(false);
      await reload();
      setToast({ type: "success", message: "Dashboard key removed — reverted to the server .env key." });
    } catch (err) {
      setToast({ type: "error", message: (err && err.message) || "Failed to remove the key." });
    } finally {
      setRemoving(false);
    }
  }

  return (
    <>
    <form onSubmit={save} className="space-y-5">
      <Toast {...(toast || {})} onClose={() => setToast(null)} />
      <LoadError message={error} onRetry={reload} className="mb-0" />

      {/* The master switch, and beneath it ONLY the key card for whichever
          provider that switch currently names. */}
      <ActiveAiPanel data={data} reload={reload} active={active} setActive={setActive} />

      {demo && <DemoBanner />}

      {encryptionMissing && (
        <div className="flex items-start gap-2.5 rounded-xl border border-rose-200 bg-rose-50/70 px-4 py-3 text-sm text-rose-700 dark:border-rose-500/25 dark:bg-rose-500/10 dark:text-rose-200">
          <ShieldAlert size={16} className="mt-0.5 shrink-0" />
          <span>
            <b>CREDENTIALS_FERNET_KEY</b> is not set on the server, so the API key can&apos;t be encrypted — saving is blocked until you add it and restart.
          </span>
        </div>
      )}

      {/* Claude's key card — shown only when Claude is the provider in force
          (or under Auto, where every saved key is in the rotation). */}
      {showsProvider(active, "anthropic") && (
      <div className="card p-6">
        <div className="flex items-center gap-3">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-brand-500/10 text-brand-600 dark:text-brand-300"><Sparkles size={18} /></span>
          <div>
            <h3 className="text-base font-semibold text-slate-800 dark:text-white">Claude / Anthropic API key</h3>
            <p className="mt-0.5 text-sm text-slate-400">Powers every agent (planner, validator, reporter, selector). Paste a key here and it overrides the server&apos;s <code className="rounded bg-slate-100 px-1 dark:bg-white/10">ANTHROPIC_API_KEY</code> — no redeploy. Stored encrypted.</p>
          </div>
        </div>

        {/* current status */}
        <div className="mt-5 flex flex-col gap-3 rounded-2xl border border-slate-100 p-4 dark:border-white/5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <span className={cn("grid h-9 w-9 shrink-0 place-items-center rounded-xl", source.cls)}><source.icon size={16} /></span>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Active key source</p>
              <p className="text-sm font-semibold text-slate-800 dark:text-white">{source.label}</p>
            </div>
          </div>
          <code className="rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-600 dark:bg-white/10 dark:text-slate-300">
            {info.key_set ? info.key_preview : "no key configured"}
          </code>
        </div>

        {/* paste new key */}
        <label className="mt-5 block">
          <span className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
            {info.source === "database" ? "Replace API key" : "Paste API key"}
          </span>
          <span className="relative block">
            <KeyRound size={18} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type={showKey ? "text" : "password"}
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder="sk-ant-api03-…"
              autoComplete="off"
              spellCheck="false"
              disabled={encryptionMissing}
              className={cn(FIELD_CLS, "pl-11 pr-11 font-mono", encryptionMissing && "opacity-60")}
            />
            <button type="button" onClick={() => setShowKey((s) => !s)} aria-label={showKey ? "Hide API key" : "Show API key"} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
              {showKey ? <Eye size={16} /> : <EyeOff size={16} />}
            </button>
          </span>
          <span className="mt-1.5 block text-xs text-slate-400">
            From console.anthropic.com → API keys. Stored encrypted; never shown again after saving.
          </span>
        </label>
      </div>
      )}

      {/* The save / test / remove row acts on the Claude key, so it travels with
          that card rather than standing alone under another provider's form. */}
      {showsProvider(active, "anthropic") && (
      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" disabled={busy || !key.trim() || encryptionMissing}>
          {busy ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />} Save API key
        </Button>
        <Button type="button" variant="outline" onClick={test} disabled={testing || !info.key_set}>
          {testing ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />} Test key
        </Button>
        {info.source === "database" && (
          <button type="button" onClick={() => setConfirmRemove(true)} disabled={removing}
            className="inline-flex h-10 items-center gap-2 rounded-xl border border-rose-200 px-4 text-sm font-semibold text-rose-600 transition-colors hover:bg-rose-50 disabled:opacity-60 dark:border-rose-500/30 dark:hover:bg-rose-500/10">
            {removing ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
            {info.env_key_set ? "Remove (revert to .env)" : "Remove key"}
          </button>
        )}
      </div>
      )}
    </form>

    {/* Outside the form on purpose: Modal renders inline, and its footer buttons
        would otherwise default to type="submit" and fire this form's onSubmit. */}
    <ConfirmDialog
      open={confirmRemove}
      title="Remove the Claude API key?"
      body={info.env_key_set
        ? "Agents revert to the server's ANTHROPIC_API_KEY. The key stored here is encrypted and can't be read back, so you'd need to paste it again."
        : "There is no .env key to fall back on — every agent run will fail until a new key is saved. This cannot be undone."}
      confirmLabel="Remove key"
      busy={removing}
      onConfirm={remove}
      onCancel={() => setConfirmRemove(false)}
    />
    </>
  );
}

export default function AdminSettings() {
  // ONE load of /admin/ai-config for this page, and one piece of provider state
  // on top of it. The sidebar hint and the AI tab below both read these, so the
  // label in the nav is always the provider the tab is actually configuring —
  // the hint used to be the fixed string "Claude key" with no connection to the
  // configuration at all.
  const ai = useApiData(getAiConfig, AI_DEFAULTS);
  const [activeAi, setActiveAi] = useState("");
  // switchTo() calls setActiveAi only after the server accepts the change, so
  // the hint follows a confirmed switch rather than a click.
  useEffect(() => {
    if (ai.data) setActiveAi(ai.data.active_provider || "");
  }, [ai.data]);

  return (
    <TesterSettings
      subtitle="Manage the platform, your profile, security and preferences."
      initialTab="ai-key"
      extraTabs={[
        {
          id: "ai-key", label: "AI / API Key", icon: KeyRound,
          hint: providerHint(activeAi),
          element: <AiKeyTab ai={ai} active={activeAi} setActive={setActiveAi} />,
        },
        { id: "smtp", label: "Email / SMTP", icon: Mail, hint: "Outbound email", element: <SmtpTab /> },
        { id: "oauth", label: "Google sign-in", icon: ShieldCheck, hint: "OAuth credentials", element: <OAuthTab /> },
      ]}
    />
  );
}
